"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StablePool = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const math_1 = require("@zenlink-interface/math");
const jsbi_1 = __importDefault(require("jsbi"));
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const util_1 = require("../../util");
const BasePool_1 = require("./BasePool");
const FEE_DENOMINATOR = jsbi_1.default.BigInt(1e10);
class StablePool extends BasePool_1.BasePool {
    constructor(swap, token0, token1, fee) {
        const token0Index = swap.getTokenIndex(token0);
        const token1Index = swap.getTokenIndex(token1);
        super(swap.contractAddress, token0, token1, fee, bignumber_1.BigNumber.from(swap.balances[token0Index].quotient.toString()), bignumber_1.BigNumber.from(swap.balances[token1Index].quotient.toString()));
        this.swap = swap;
        this.token0Index = token0Index;
        this.token1Index = token1Index;
    }
    updateSwap(swap) {
        (0, tiny_invariant_1.default)(swap.contractAddress === this.swap.contractAddress, 'StablePool: Different pool!');
        this.swap = swap;
        const res0 = bignumber_1.BigNumber.from(swap.balances[this.token0Index].quotient.toString());
        const res1 = bignumber_1.BigNumber.from(swap.balances[this.token1Index].quotient.toString());
        if (!res0.eq(this.reserve0) || !res1.eq(this.reserve1))
            this.updateReserves(res0, res1);
    }
    getOutput(amountIn, direction) {
        const inIndex = direction ? this.token0Index : this.token1Index;
        const outIndex = direction ? this.token1Index : this.token0Index;
        const inAmountBN = (0, util_1.getBigNumber)(amountIn);
        const normalizedBalances = this.swap.xp;
        const newInBalance = jsbi_1.default.add(normalizedBalances[inIndex], jsbi_1.default.multiply(jsbi_1.default.BigInt(inAmountBN.toString()), this.swap.tokenMultipliers[inIndex]));
        const outBalance = this.swap._getY(inIndex, outIndex, newInBalance, normalizedBalances);
        const outAmount = jsbi_1.default.divide(jsbi_1.default.subtract(jsbi_1.default.subtract(normalizedBalances[outIndex], outBalance), math_1.ONE), this.swap.tokenMultipliers[outIndex]);
        const fee = jsbi_1.default.divide(jsbi_1.default.multiply(this.swap.swapFee, outAmount), FEE_DENOMINATOR);
        return {
            output: Number.parseInt(jsbi_1.default.subtract(outAmount, fee).toString()),
            gasSpent: this.swapGasCost,
        };
    }
    getInput(amountOut, direction) {
        const inIndex = direction ? this.token0Index : this.token1Index;
        const outIndex = direction ? this.token1Index : this.token0Index;
        const outAmountBN = (0, util_1.getBigNumber)(amountOut);
        const normalizedBalances = this.swap.xp;
        let newOutBalance = jsbi_1.default.subtract(normalizedBalances[outIndex], jsbi_1.default.multiply(jsbi_1.default.BigInt(outAmountBN.toString()), this.swap.tokenMultipliers[outIndex]));
        if (jsbi_1.default.lessThan(newOutBalance, math_1.ONE))
            // lack of precision
            newOutBalance = math_1.ONE;
        const inNewBalance = this.swap._getY(outIndex, inIndex, newOutBalance, normalizedBalances);
        const inAmount = jsbi_1.default.divide(jsbi_1.default.add(jsbi_1.default.subtract(inNewBalance, normalizedBalances[inIndex]), math_1.ONE), this.swap.tokenMultipliers[inIndex]);
        const inAmountWithFee = jsbi_1.default.divide(inAmount, jsbi_1.default.subtract(math_1.ONE, jsbi_1.default.divide(this.swap.swapFee, FEE_DENOMINATOR)));
        return {
            input: Number.parseInt(inAmountWithFee.toString()),
            gasSpent: this.swapGasCost,
        };
    }
    calcCurrentPriceWithoutFee(direction) {
        const inIndex = direction ? this.token0Index : this.token1Index;
        return this.calcPrice(10 ** this.swap.getToken(inIndex).decimals, direction);
    }
    calcPrice(amountIn, direction) {
        (0, tiny_invariant_1.default)(amountIn !== 0, 'StablePool: Invalid zero amountIn');
        const inIndex = direction ? this.token0Index : this.token1Index;
        const outIndex = direction ? this.token1Index : this.token0Index;
        const normalizedBalances = this.swap.xp;
        const inAmount = jsbi_1.default.add(normalizedBalances[inIndex], jsbi_1.default.multiply(jsbi_1.default.BigInt((0, util_1.getBigNumber)(amountIn)), this.swap.tokenMultipliers[inIndex]));
        const outBalance = this.swap._getY(inIndex, outIndex, inAmount, normalizedBalances);
        const outAmount = jsbi_1.default.divide(jsbi_1.default.subtract(jsbi_1.default.subtract(normalizedBalances[outIndex], outBalance), math_1.ONE), this.swap.tokenMultipliers[outIndex]);
        return Number.parseInt(outAmount.toString()) / amountIn;
    }
}
exports.StablePool = StablePool;
